import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Card, Typography, Space, notification } from 'antd';
import { UserOutlined, TableOutlined, SwapOutlined, SaveOutlined, ReloadOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import StudentManager from './StudentManager';
import SeatingLayout from './SeatingLayout';
import SortingOptions from './SortingOptions';
import SeatNumberControls from './SeatNumberControls';

const { Title } = Typography;

const SeatingArrangement = () => {
  const [students, setStudents] = useState([]);
  const [seatingLayout, setSeatingLayout] = useState([]);
  const [rows, setRows] = useState(6);
  const [cols, setCols] = useState(8);
  const [sortingMode, setSortingMode] = useState('random');
  const [isArranging, setIsArranging] = useState(false);
  const [showSeatNumbers, setShowSeatNumbers] = useState(true);
  const [seatNumberColor, setSeatNumberColor] = useState('#374151');

  // 初始化座位布局
  useEffect(() => {
    initializeSeating();
  }, [rows, cols]);

  // 从本地存储加载数据
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  const initializeSeating = () => {
    const layout = Array(rows).fill(null).map((_, rowIndex) =>
      Array(cols).fill(null).map((_, colIndex) => ({
        row: rowIndex,
        col: colIndex,
        student: null,
        isEmpty: false // 可以设置某些座位为空（如讲台前方）
      }))
    );
    setSeatingLayout(layout);
  };

  const loadFromLocalStorage = () => {
    const savedData = localStorage.getItem('seatingData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.students) setStudents(data.students);
        if (data.seatingLayout) setSeatingLayout(data.seatingLayout);
        if (data.rows) setRows(data.rows);
        if (data.cols) setCols(data.cols);
        if (data.sortingMode) setSortingMode(data.sortingMode);
      } catch (error) {
        console.error('加载座位数据失败:', error);
      }
    }
  };

  const saveToLocalStorage = () => {
    const data = {
      students,
      seatingLayout,
      rows,
      cols,
      sortingMode,
      showSeatNumbers,
      seatNumberColor,
      saveDate: new Date().toISOString()
    };
    localStorage.setItem('seatingData', JSON.stringify(data));
    notification.success({
      message: '保存成功',
      description: '座位安排已保存到本地存储',
    });
  };

  const exportToHTML = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>座位安排表</title>
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
            margin: 20px; 
            background: #f5f5f5;
        }
        .container { 
            max-width: 1200px; 
            margin: 0 auto; 
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .title { 
            text-align: center; 
            color: #1890ff;
            margin-bottom: 30px;
        }
        .blackboard {
            text-align: center;
            margin-bottom: 30px;
        }
        .blackboard-text {
            background: #2d3748;
            color: white;
            padding: 15px 40px;
            border-radius: 8px;
            display: inline-block;
            font-size: 18px;
            font-weight: bold;
        }
        .seating-grid {
            display: grid;
            grid-template-columns: repeat(${cols}, 1fr);
            gap: 10px;
            max-width: fit-content;
            margin: 0 auto;
        }
        .seat {
            width: 60px;
            height: 60px;
            border: 2px solid #d1d5db;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            position: relative;
        }
        .seat.occupied {
            background: #eff6ff;
            border-color: #3b82f6;
        }
        .seat.male {
            background: #dbeafe;
            border-color: #3b82f6;
        }
        .seat.female {
            background: #fce7f3;
            border-color: #ec4899;
        }
        .seat.empty {
            background: #f9fafb;
        }
        .seat.locked {
            background: #e5e7eb;
            border-color: #9ca3af;
            color: #6b7280;
        }
        .seat-number {
            position: absolute;
            bottom: -2px;
            right: -2px;
            background: ${seatNumberColor};
            color: white;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
        }
        .student-name {
            font-weight: 600;
            text-align: center;
            line-height: 1.2;
        }
        .export-info {
            text-align: center;
            margin-top: 30px;
            color: #6b7280;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1 class="title">📍 教室座位安排表</h1>
        
        <div class="blackboard">
            <div class="blackboard-text">讲台</div>
        </div>
        
        <div class="seating-grid">
            ${seatingLayout.map((row, rowIndex) =>
                row.map((seat, colIndex) => {
                    const seatNum = rowIndex * cols + colIndex + 1;
                    if (seat.isEmpty) {
                        return `<div class="seat locked">
                            <div>🔒</div>
                            <div style="font-size: 10px;">锁定</div>
                            ${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}
                        </div>`;
                    } else if (seat.student) {
                        const genderClass = seat.student.gender === 'female' ? 'female' : 'male';
                        return `<div class="seat occupied ${genderClass}">
                            <div class="student-name">${seat.student.name}</div>
                            ${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}
                        </div>`;
                    } else {
                        return `<div class="seat empty">
                            <div>👤</div>
                            <div style="font-size: 10px;">空位</div>
                            ${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}
                        </div>`;
                    }
                }).join('')
            ).join('')}
        </div>
        
        <div class="export-info">
            <p>导出时间: ${new Date().toLocaleString()}</p>
            <p>座位规模: ${rows} 行 × ${cols} 列 (共 ${rows * cols} 个座位)</p>
            <p>已安排学生: ${seatingLayout.flat().filter(seat => seat.student).length} 人</p>
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `座位安排表_${new Date().toLocaleDateString().replace(/\//g, '-')}.html`;
    link.click();
    URL.revokeObjectURL(url);
    
    notification.success({
      message: 'HTML导出成功',
      description: '座位表已导出为HTML文件，可用浏览器打开查看',
    });
  };

  // 打印当前座位安排：打开新窗口渲染静态页面并自动调用浏览器打印
  const printArrangement = () => {
    const printHtml = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>打印 - 座位安排表</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 24px; }
    .title { text-align: center; color: #1890ff; margin-bottom: 18px; }
    .blackboard { text-align: center; margin-bottom: 18px; }
    .blackboard-text { background: #2d3748; color: #fff; padding: 10px 24px; border-radius: 8px; display: inline-block; font-weight: 700; }
    .seating-grid { display: grid; grid-template-columns: repeat(${cols}, 1fr); gap: 8px; max-width: fit-content; margin: 0 auto; }
    .seat { width: 54px; height: 54px; border: 2px solid #d1d5db; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 12px; position: relative; }
    .seat.male { background: #dbeafe; border-color: #3b82f6; }
    .seat.female { background: #fce7f3; border-color: #ec4899; }
    .seat.empty { background: #f9fafb; }
    .seat.locked { background: #e5e7eb; border-color: #9ca3af; color: #6b7280; }
    .seat-number { position: absolute; bottom: -2px; right: -2px; background: ${seatNumberColor}; color: #fff; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 10px; }
    .info { text-align: center; margin-top: 16px; color: #6b7280; font-size: 12px; }
    @media print { body { margin: 6mm; } .info { display: none; } }
  </style>
</head>
<body>
  <h2 class="title">📍 教室座位安排表</h2>
  <div class="blackboard"><div class="blackboard-text">讲台</div></div>
  <div class="seating-grid">
    ${seatingLayout.map((row, rIdx) => row.map((seat, cIdx) => {
      const seatNum = rIdx * cols + cIdx + 1;
      if (seat.isEmpty) {
        return `<div class="seat locked"><div>🔒</div><div style="font-size:10px;">锁定</div>${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}</div>`;
      } else if (seat.student) {
        const genderClass = seat.student.gender === 'female' ? 'female' : 'male';
        return `<div class="seat ${genderClass}"><div style="font-weight:600;">${seat.student.name}</div>${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}</div>`;
      } else {
        return `<div class="seat empty"><div>👤</div><div style="font-size:10px;">空位</div>${showSeatNumbers ? `<div class="seat-number">${seatNum}</div>` : ''}</div>`;
      }
    }).join('')).join('')}
  </div>
  <div class="info">打印时间: ${new Date().toLocaleString()} · 规模: ${rows}行 × ${cols}列 · 已安排: ${seatingLayout.flat().filter(s => s.student).length} 人</div>
  <script>
    window.onload = function() { setTimeout(function(){ window.print(); }, 50); };
  </script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.open();
      win.document.write(printHtml);
      win.document.close();
      try { win.focus(); } catch (_) {}
    } else {
      notification.warning({ message: '无法打开打印窗口', description: '请检查浏览器是否拦截了弹窗' });
    }
  };

  // 座位排序算法
  const arrangeSeatsByMode = async (mode) => {
    setIsArranging(true);
    
    // 获取可用座位（排除被标记为空的座位）
    const availableSeats = [];
    seatingLayout.forEach((row, rowIndex) => {
      row.forEach((seat, colIndex) => {
        if (!seat.isEmpty) {
          availableSeats.push({ row: rowIndex, col: colIndex });
        }
      });
    });

    if (students.length > availableSeats.length) {
      notification.warning({
        message: '座位不足',
        description: `学生数量(${students.length})超过可用座位数量(${availableSeats.length})`,
      });
      setIsArranging(false);
      return;
    }

    let arrangedStudents = [];
    
    switch (mode) {
      case 'random':
        arrangedStudents = shuffleArray([...students]);
        break;
      case 'height':
        arrangedStudents = [...students].sort((a, b) => (a.height || 0) - (b.height || 0));
        break;
      case 'grade':
        arrangedStudents = [...students].sort((a, b) => (b.grade || 0) - (a.grade || 0));
        break;
      case 'alphabetical':
        arrangedStudents = [...students].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'gender_balanced':
        arrangedStudents = balanceByGender([...students]);
        break;
      default:
        arrangedStudents = [...students];
    }

    // 清空当前座位安排
    const newLayout = seatingLayout.map(row =>
      row.map(seat => ({ ...seat, student: null }))
    );

    // 为了视觉效果，添加动画延迟
    for (let i = 0; i < arrangedStudents.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 50));
      const seat = availableSeats[i];
      newLayout[seat.row][seat.col].student = arrangedStudents[i];
      setSeatingLayout([...newLayout]);
    }

    setIsArranging(false);
    notification.success({
      message: '排序完成',
      description: `使用${getSortingModeName(mode)}完成座位安排`,
    });
  };

  // 工具函数
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const balanceByGender = (students) => {
    const boys = students.filter(s => s.gender === 'male');
    const girls = students.filter(s => s.gender === 'female');
    const balanced = [];
    
    const maxLength = Math.max(boys.length, girls.length);
    for (let i = 0; i < maxLength; i++) {
      if (boys[i]) balanced.push(boys[i]);
      if (girls[i]) balanced.push(girls[i]);
    }
    
    return balanced;
  };

  const getSortingModeName = (mode) => {
    const names = {
      random: '随机排序',
      height: '身高排序',
      grade: '成绩排序',
      alphabetical: '姓名排序',
      gender_balanced: '男女均衡'
    };
    return names[mode] || '未知排序';
  };

  const clearArrangement = () => {
    const clearedLayout = seatingLayout.map(row =>
      row.map(seat => ({ ...seat, student: null }))
    );
    setSeatingLayout(clearedLayout);
    notification.info({
      message: '已清空',
      description: '座位安排已清空',
    });
  };

  const exportArrangement = () => {
    const exportData = {
      students,
      seatingLayout,
      rows,
      cols,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `座位安排_${new Date().toLocaleDateString().replace(/\//g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    notification.success({
      message: '导出成功',
      description: '座位安排已导出为JSON文件',
    });
  };

  return (
    <div className="p-6">
      <div className="w-full px-4">

        <Row gutter={16}>
          {/* 左侧：学生管理 */}
          <Col xs={24} lg={5}>
            <StudentManager 
              students={students} 
              onStudentsChange={setStudents}
              seatingLayout={seatingLayout}
            />
          </Col>

          {/* 中间：座位布局 */}
          <Col xs={24} lg={14}>
            <Card 
              title={
                <div className="flex justify-between items-center">
                  <span>
                    <TableOutlined className="mr-2" />
                    教室座位图 ({rows} × {cols})
                  </span>
                  <Space>
                    <Button 
                      icon={<PrinterOutlined />} 
                      onClick={printArrangement}
                      type="primary"
                      ghost
                    >
                      打印
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={clearArrangement}
                      type="default"
                    >
                      清空
                    </Button>
                    <Button 
                      icon={<SaveOutlined />} 
                      onClick={saveToLocalStorage}
                      type="primary"
                    >
                      保存
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={exportArrangement}
                    >
                      导出JSON
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={exportToHTML}
                      type="primary"
                      ghost
                    >
                      导出HTML
                    </Button>
                  </Space>
                </div>
              }
              className="overflow-visible"
              styles={{ body: { padding: 0, overflow: 'visible' } }}
            >
              <SeatingLayout 
                seatingLayout={seatingLayout}
                onSeatingChange={setSeatingLayout}
                students={students}
                isArranging={isArranging}
                showSeatNumbers={showSeatNumbers}
                seatNumberColor={seatNumberColor}
              />
            </Card>
          </Col>

          {/* 右侧：排序选项 */}
          <Col xs={24} lg={5}>
            <Card 
              title="排序选项"
            >
              <SortingOptions 
                sortingMode={sortingMode}
                onSortingModeChange={setSortingMode}
                onArrange={() => arrangeSeatsByMode(sortingMode)}
                isArranging={isArranging}
                rows={rows}
                cols={cols}
                onRowsChange={setRows}
                onColsChange={setCols}
              />
              <SeatNumberControls 
                showSeatNumbers={showSeatNumbers}
                onToggleNumbers={setShowSeatNumbers}
                seatNumberColor={seatNumberColor}
                onColorChange={setSeatNumberColor}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SeatingArrangement;
